import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency } from '../utils/helpers';

const Invoices = ({ onDisplayInvoice }) => {
  const { invoices } = useApp();
  const [searchTerm, setSearchTerm] = useState('');

  // Debug: Log when invoices change
  useEffect(() => {
    console.log('Invoices component - invoices updated:', invoices.length);
    if (invoices.length > 0) {
      console.log('First invoice:', invoices[0]);
      console.log('All invoice numbers:', invoices.map(inv => inv.number));
    } else {
      console.log('No invoices in state');
    }
  }, [invoices]);

  const matchesSearch = (inv, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [
      inv.number || '',
      inv.reg || '',
      inv.vehicle || '',
      inv.contact || '',
      formatCurrency(inv.total || 0)
    ]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(q));
  };

  const filteredInvoices = useMemo(() => {
    // Filter by search term and sort by date (most recent first)
    return invoices
      .filter(inv => matchesSearch(inv, searchTerm))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [invoices, searchTerm]);

  const handleInvoiceClick = (inv) => {
    if (onDisplayInvoice) {
      onDisplayInvoice(inv);
    }
  };

  const handleExportCSV = () => {
    if (filteredInvoices.length === 0) {
      alert('No invoices to export.');
      return;
    }

    // Define CSV headers
    const headers = [
      'Invoice Number',
      'Date',
      'Registration',
      'Vehicle',
      'Contact',
      'MOT Status',
      'Drop-off Date',
      'Collection Date',
      'Summary',
      'Subtotal',
      'VAT',
      'Total'
    ];

    // Convert invoices to CSV rows
    const csvRows = [
      headers.join(','),
      ...filteredInvoices.map(inv => {
        const date = new Date(inv.date);
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });

        // Escape commas and quotes in CSV values
        const escapeCSV = (value) => {
          if (value == null) return '';
          const str = String(value);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        };

        return [
          escapeCSV(inv.number || ''),
          escapeCSV(formattedDate),
          escapeCSV(inv.reg || ''),
          escapeCSV(inv.vehicle || ''),
          escapeCSV(inv.contact || ''),
          escapeCSV(inv.motStatus || ''),
          escapeCSV(inv.dropOffDate || ''),
          escapeCSV(inv.collectionDate || ''),
          escapeCSV(inv.summary || ''),
          escapeCSV((inv.subtotal || 0).toFixed(2)),
          escapeCSV((inv.vat || 0).toFixed(2)),
          escapeCSV((inv.total || 0).toFixed(2))
        ].join(',');
      })
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Generate filename with current date
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    const filename = `invoices_export_${dateStr}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    URL.revokeObjectURL(url);
  };

  return (
    <div className="vehicle-inner invoices-page">
      <div className="vehicle-header">
        <div>
          <h2>Invoice History</h2>
          <p className="vehicle-subtitle">All invoices from the past year</p>
        </div>
        <div className="vehicle-controls" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label className="search-field">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M10.5 3.75a6.75 6.75 0 1 1 0 13.5 6.75 6.75 0 0 1 0-13.5Zm0 1.5a5.25 5.25 0 1 0 0 10.5 5.25 5.25 0 0 0 0-10.5Zm7.97 11.28 3.15 3.15-1.06 1.06-3.15-3.15 1.06-1.06Z" fill="#4f8df3"/>
            </svg>
            <input 
              id="invoiceSearch" 
              type="search" 
              placeholder="Search by invoice number, registration, vehicle or contact..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
          {filteredInvoices.length > 0 && (
            <button
              type="button"
              onClick={handleExportCSV}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '2px solid var(--primary)',
                background: 'var(--primary)',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--primary-hover, #2d5a8f)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'var(--primary)';
              }}
              title="Export invoices to CSV"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Export CSV
            </button>
          )}
        </div>
      </div>
      <div
        id="invoiceHistoryContainerPage"
        style={{
          background: 'var(--card-bg)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid var(--border)'
        }}
      >
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: 'var(--text-dark)',
          margin: '0 0 20px 0'
        }}>
          All Invoices
          {filteredInvoices.length > 0 && (
            <span style={{ fontSize: '14px', fontWeight: '400', color: 'var(--text-muted)', marginLeft: '12px' }}>
              ({filteredInvoices.length})
            </span>
          )}
        </h3>
        <div className="invoice-history-container">
          {filteredInvoices.length === 0 ? (
            <div className="empty-state">
              No invoices found.
            </div>
          ) : (
            filteredInvoices.map((inv) => {
              const date = new Date(inv.date);
              const invoiceKey = inv.number || `invoice-${inv.reg}-${inv.date}`;
              return (
                <div
                  key={invoiceKey}
                  className="invoice-history-card"
                  onClick={() => handleInvoiceClick(inv)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleInvoiceClick(inv); }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-dark)' }}>
                        {inv.number}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--text-dark)', marginTop: '4px' }}>
                        <strong>{inv.reg}</strong> – {inv.vehicle}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{inv.contact}</div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                      {formatCurrency(inv.total)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Invoices;
